import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getRoleHomePath } from '@/lib/roleNavigation';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { authModeAPI, publicAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Shield, Scale, Building2, Lock, Mail, ArrowRight, KeyRound, Brain, BookOpen, Video, Award, Zap, Users, FileText, Search, X, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import ThemeToggle from '@/components/ThemeToggle';

// Marketing sayfaları (Kurumsal / Bireysel) şimdilik gizli.
// Açmak için: true yap. Sayfalar ve route'lar yerinde duruyor.
const SHOW_MARKETING_LINKS = false;

// ==================== Kurumsal Giriş Modalı ====================
// Kullanıcı sadece kurumsal e-posta adresini girer; backend domainden
// tenant'ı otomatik çözer. Müşteri firmaların listesi kimseye gösterilmez —
// bu kararın sebebi rakip-müşteri çapraz görünürlüğünü ve hedefli oltalama
// riskini ortadan kaldırmak. Önceki /public/tenants/sso-list ucu backend'den
// kaldırıldı; yalnızca find-by-domain çözümü kullanılır.
function CompanySelectModal({ onClose, onSelect }) {
    const [email, setEmail] = useState('');
    const [searching, setSearching] = useState(false);
    const API = process.env.REACT_APP_BACKEND_URL;

    const handleEmailSearch = async () => {
        if (!email.trim() || !email.includes('@')) {
            toast.error('Lütfen geçerli bir kurumsal e-posta adresi giriniz.');
            return;
        }
        setSearching(true);
        try {
            const r = await fetch(`${API}/api/public/tenant/find-by-domain?domain=${encodeURIComponent(email)}`);
            if (r.ok) {
                const data = await r.json();
                onSelect(data);
            } else {
                toast.error('Bu e-posta alanı için SSO tanımlı kurum bulunamadı.');
            }
        } catch { toast.error('İşlem sırasında bir hata oluştu.'); }
        finally { setSearching(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="bg-background border border-border rounded-2xl p-6 w-full max-w-md shadow-2xl"
                onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <h2 className="text-lg font-bold">Kurumsal Giriş</h2>
                        <p className="text-xs text-muted-foreground mt-0.5">Kurumsal e-posta adresinizi giriniz; kurumunuz otomatik olarak tespit edilecektir.</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="space-y-2">
                    <Label className="text-xs">Kurumsal e-posta adresiniz</Label>
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/40" />
                            <Input type="email" placeholder="ornek@sirket.com.tr" value={email} onChange={e => setEmail(e.target.value)}
                                className="pl-9 h-10" onKeyDown={e => e.key === 'Enter' && handleEmailSearch()}
                                data-testid="company-email-input" autoFocus />
                        </div>
                        <Button size="sm" onClick={handleEmailSearch} disabled={searching || !email} className="h-10 px-4" data-testid="company-search-btn">
                            {searching ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Search className="w-4 h-4 mr-1.5" /> Devam</>}
                        </Button>
                    </div>
                    <p className="text-[10px] text-muted-foreground/70 pt-1">
                        Kurumunuz kurumsal kimlik doğrulama (SSO) sayfasına yönlendirilecektir.
                    </p>
                </div>
            </motion.div>
        </div>
    );
}

const LoginPage = () => {
    const navigate = useNavigate();
    const { login, loginWithKeycloak, loading: authLoading, error: authError } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [ssoLoading, setSsoLoading] = useState(false);
    const [authMode, setAuthMode] = useState(null);
    const [stats, setStats] = useState({ question_count: 3873, lesson_count: 205, module_count: 56 });
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [showForgotModal, setShowForgotModal] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [showCompanyModal, setShowCompanyModal] = useState(false);

    const handleForgotPassword = async () => {
        if (!forgotEmail || !forgotEmail.includes('@')) { toast.error('Lütfen geçerli bir e-posta adresi giriniz.'); return; }
        try {
            const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/forgot-password`, {
                method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({email: forgotEmail})
            });
            const d = await r.json();
            toast.success(d.message || 'Şifre sıfırlama bağlantısı gönderildi.');
            setShowForgotModal(false); setForgotEmail('');
        } catch { toast.error('İşlem sırasında bir hata oluştu.'); }
    };

    useEffect(() => {
        authModeAPI.getMode().then(r => setAuthMode(r.data?.mode || 'local')).catch(() => setAuthMode('local'));
        publicAPI.getStats().then(r => setStats(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (authError && ssoLoading) { toast.error(authError); setSsoLoading(false); }
    }, [authError, ssoLoading]);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!loginEmail || !loginPassword) { toast.error('Lütfen tüm alanları doldurun.'); return; }
        setIsLoading(true);
        try {
            const userData = await login(loginEmail, loginPassword);
            toast.success('Giriş başarılı.');
            setTimeout(() => {
                navigate(getRoleHomePath(userData.role));
            }, 100);
        } catch (err) { toast.error(err.message); }
        finally { setIsLoading(false); }
    };

    const FEATURES = [
        { icon: Brain, title: 'Mevzuat Asistanı', desc: 'Yapay zekâ destekli mevzuat soru-cevap', color: 'from-violet-500/20 to-violet-600/5 border-violet-500/20 text-violet-400' },
        { icon: FileText, title: 'Geniş Mevzuat Havuzu', desc: 'MASAK, TCMB, BDDK, KVKK doküman arşivi', color: 'from-blue-500/20 to-blue-600/5 border-blue-500/20 text-blue-400' },
        { icon: Video, title: 'Canlı Ders Altyapısı', desc: 'BigBlueButton ile interaktif eğitim oturumları', color: 'from-red-500/20 to-red-600/5 border-red-500/20 text-red-400' },
        { icon: Award, title: 'Sertifikasyon', desc: 'MASAK 5549 SK Md.24 uyumlu sertifikasyon', color: 'from-amber-500/20 to-amber-600/5 border-amber-500/20 text-amber-400' },
        { icon: Zap, title: 'Yapay Zekâ Destekli İçerik', desc: 'Kart, soru bankası ve sesli içerik üretimi', color: 'from-emerald-500/20 to-emerald-600/5 border-emerald-500/20 text-emerald-400' },
        { icon: Users, title: 'Kurumsal Çoklu Yapı', desc: 'Her kurum için izole edilmiş eğitim ortamı', color: 'from-cyan-500/20 to-cyan-600/5 border-cyan-500/20 text-cyan-400' },
    ];

    return (
        <div className="min-h-screen bg-background relative overflow-hidden" data-testid="login-page">
            {/* Background */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_50%,hsl(220_90%_56%/0.08),transparent_50%),radial-gradient(ellipse_at_80%_20%,hsl(280_70%_60%/0.05),transparent_50%)]" />
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

            {/* Theme Toggle */}
            <div className="absolute top-4 right-4 z-50">
                <ThemeToggle />
            </div>

            <div className="relative z-10 min-h-screen flex flex-col lg:flex-row max-w-[1440px] mx-auto w-full">
                {/* LEFT - Branding + Features (60%) */}
                <div className="lg:w-[58%] flex flex-col p-6 lg:p-12 xl:p-16">
                    {/* Nav */}
                    <motion.div className="flex items-center justify-between mb-12 lg:mb-16"
                        initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                        <Link to="/" className="flex items-center gap-3 group" title="Ana sayfaya dön">
                            <div className="w-9 h-9 rounded-lg bg-primary/15 border border-primary/25 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                <Shield className="w-4.5 h-4.5 text-primary" />
                            </div>
                            <span className="text-lg font-bold font-display group-hover:text-primary/90 transition-colors">Uyum<span className="text-primary">Akademi</span></span>
                        </Link>
                        {SHOW_MARKETING_LINKS && (
                            <div className="hidden sm:flex items-center gap-3">
                                <a href="/kurumsal">
                                    <Button variant="outline" size="sm" className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10">
                                        <Building2 className="w-3.5 h-3.5 mr-1.5" />Kurumsal Çözümler
                                    </Button>
                                </a>
                                <a href="/bireysel">
                                    <Button variant="outline" size="sm" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
                                        <ArrowRight className="w-3.5 h-3.5 mr-1.5" />Bireysel Başvuru
                                    </Button>
                                </a>
                            </div>
                        )}
                    </motion.div>

                    {/* Hero */}
                    <div className="flex-1 flex flex-col justify-center max-w-2xl">
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-medium text-primary mb-6">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                Türkiye'nin Kurumsal Uyum Eğitim Platformu
                            </div>
                            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-extrabold font-display tracking-tight leading-[1.05] mb-5">
                                Finansal Uyum<br />
                                <span className="text-gradient">Eğitiminde Yeni Dönem</span>
                            </h1>
                            <p className="text-base lg:text-lg text-muted-foreground leading-relaxed max-w-xl mb-10">
                                MASAK, TCMB, BDDK ve KVKK mevzuatı kapsamındaki yükümlülükler; yapay zekâ destekli eğitim, canlı dersler ve interaktif içeriklerle kurumsal süreçlerinize entegre edilir.
                            </p>
                        </motion.div>

                        {/* Feature Grid */}
                        <motion.div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-10"
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                            {FEATURES.map((f, i) => (
                                <motion.div key={i}
                                    className={`p-3.5 rounded-xl bg-gradient-to-br ${f.color} border backdrop-blur-sm`}
                                    initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.35 + i * 0.07 }}>
                                    <f.icon className="w-5 h-5 mb-2" />
                                    <div className="text-xs font-bold text-foreground">{f.title}</div>
                                    <div className="text-[10px] text-muted-foreground mt-0.5 leading-snug">{f.desc}</div>
                                </motion.div>
                            ))}
                        </motion.div>

                        {/* Stats Row */}
                        <motion.div className="flex gap-8 lg:gap-12"
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
                            {[
                                { v: `${stats.question_count}+`, l: 'Soru' },
                                { v: stats.lesson_count, l: 'Ders' },
                                { v: stats.module_count, l: 'Modül' },
                                { v: '38K+', l: 'Mevzuat Kaydı' },
                            ].map((s, i) => (
                                <div key={i}>
                                    <div className="text-xl lg:text-2xl font-bold text-foreground">{s.v}</div>
                                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{s.l}</div>
                                </div>
                            ))}
                        </motion.div>
                    </div>
                </div>

                {/* RIGHT - Auth (42%) */}
                <div className="lg:w-[42%] flex items-center justify-center p-6 lg:p-12 lg:border-l border-border/30">
                    <motion.div className="w-full max-w-[380px]"
                        initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>

                        <div className="mb-6">
                            <h2 className="text-xl font-bold font-display">Kullanıcı Girişi</h2>
                            <p className="text-sm text-muted-foreground mt-1">Kurumsal eğitim platformuna erişim</p>
                        </div>

                        {/* SSO - always visible */}
                        <Button onClick={() => setShowCompanyModal(true)}
                            className="w-full h-11 bg-gradient-to-r from-indigo-600 to-blue-600 hover:opacity-90 shadow-lg shadow-indigo-500/20 mb-4"
                            data-testid="keycloak-login-btn">
                            <Building2 className="w-4 h-4 mr-2" />
                            Kurumsal Hesap ile Giriş
                        </Button>
                        <div className="relative mb-4">
                            <Separator />
                            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-3 text-[10px] text-muted-foreground">veya</span>
                        </div>

                        {/* Login Form */}
                        <form onSubmit={handleLogin} className="space-y-3.5">
                            <div className="space-y-1.5">
                                <Label className="text-xs">E-posta</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/40" />
                                    <Input type="email" placeholder="ornek@sirket.com" className="pl-10 h-10 bg-muted/20"
                                        value={loginEmail} onChange={e => setLoginEmail(e.target.value)} data-testid="login-email-input" />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Şifre</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/40" />
                                    <Input type="password" placeholder="••••••••" className="pl-10 h-10 bg-muted/20"
                                        value={loginPassword} onChange={e => setLoginPassword(e.target.value)} data-testid="login-password-input" />
                                </div>
                            </div>
                            <Button type="submit" className="w-full h-10 font-semibold" disabled={isLoading} data-testid="login-submit-btn">
                                {isLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    : <><span>Giriş Yap</span><ArrowRight className="w-4 h-4 ml-2" /></>}
                            </Button>
                        </form>

                        {/* Forgot password */}
                        <div className="text-center mt-3">
                            {showForgotModal ? (
                                <div className="mt-3 p-4 rounded-xl border border-border/50 bg-muted/20 space-y-3 text-left">
                                    <p className="text-xs font-medium text-foreground">Şifre Sıfırlama</p>
                                    <input
                                        type="email"
                                        placeholder="E-posta adresiniz"
                                        value={forgotEmail}
                                        onChange={e => setForgotEmail(e.target.value)}
                                        className="w-full px-3 py-2 rounded-lg bg-muted/30 border border-border text-sm text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary/50"
                                        data-testid="forgot-email-input"
                                    />
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={handleForgotPassword}
                                            className="flex-1 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
                                            data-testid="forgot-send-btn"
                                        >Gönder</button>
                                        <button
                                            type="button"
                                            onClick={() => { setShowForgotModal(false); setForgotEmail(''); }}
                                            className="px-3 py-2 rounded-lg border border-border text-xs text-muted-foreground hover:bg-muted/30 transition-colors"
                                        >İptal</button>
                                    </div>
                                </div>
                            ) : (
                                <button type="button" className="text-xs text-muted-foreground hover:text-primary transition-colors"
                                    onClick={() => setShowForgotModal(true)}>Şifremi Unuttum</button>
                            )}
                        </div>

                        {/* Demo hesapları - sadece yerel dev ortamında */}
                        {(window.location.hostname === 'localhost' ||
                          window.location.hostname === '127.0.0.1') && (
                        <div className="mt-5 pt-4 border-t border-border/30">
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground/40 font-medium mb-2">Demo Hesapları</p>
                            <div className="grid grid-cols-2 gap-1.5">
                                {[
                                    { l: 'Super Admin', e: 'demo.superadmin@uyumakademi.com', p: 'test1234', c: 'text-red-400', t: 'demo-super-admin' },
                                    { l: 'Tenant Admin', e: 'demo.tenantadmin@uyumakademi.com', p: 'test1234', c: 'text-purple-400', t: 'demo-tenant-admin' },
                                    { l: 'Denetçi', e: 'demo.auditor@uyumakademi.com', p: 'test1234', c: 'text-amber-400', t: 'demo-auditor' },
                                    { l: 'Uyum Sorumlusu', e: 'demo.compliance@uyumakademi.com', p: 'test1234', c: 'text-orange-400', t: 'demo-compliance' },
                                    { l: 'Yönetici', e: 'demo.manager@uyumakademi.com', p: 'test1234', c: 'text-cyan-400', t: 'demo-manager' },
                                    { l: 'Üst Yönetim', e: 'demo.executive@uyumakademi.com', p: 'test1234', c: 'text-indigo-400', t: 'demo-executive' },
                                    { l: 'Öğretmen', e: 'demo.instructor@uyumakademi.com', p: 'test1234', c: 'text-teal-400', t: 'demo-instructor' },
                                    { l: 'Öğrenci', e: 'demo.ogrenci@uyumakademi.com', p: 'test1234', c: 'text-emerald-400', t: 'demo-user' },
                                ].map(d => (
                                    <button key={d.t} type="button" data-testid={d.t}
                                        onClick={() => { setLoginEmail(d.e); setLoginPassword(d.p); }}
                                        className="text-left px-2.5 py-1.5 rounded-lg bg-muted/15 border border-border/40 hover:border-primary/20 hover:bg-muted/30 transition-all">
                                        <span className={`text-[10px] font-semibold ${d.c}`}>{d.l}</span>
                                        <p className="text-[9px] text-muted-foreground/60 truncate">{d.e}</p>
                                        <p className="text-[9px] text-muted-foreground/40">Şifre: {d.p}</p>
                                    </button>
                                ))}
                            </div>
                        </div>
                        )}

                        <div className="mt-5 pt-4 border-t border-border/20 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground/60">
                            <a href="mailto:destek@akademiai.com.tr" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                                <HelpCircle className="w-3 h-3" /> Yardım & Destek
                            </a>
                            <span className="text-muted-foreground/30">·</span>
                            <Link to="/kullanim-sartlari" className="hover:text-foreground transition-colors">
                                Kullanım Şartları
                            </Link>
                            <span className="text-muted-foreground/30">·</span>
                            <Link to="/kvkk-aydinlatma-metni" className="hover:text-foreground transition-colors">
                                KVKK
                            </Link>
                        </div>
                        <p className="text-center text-[9px] text-muted-foreground/30 mt-3">
                            Devam ederek kullanım şartlarını kabul etmiş olursunuz.
                        </p>
                        <p className="text-center text-[9px] text-muted-foreground/40 mt-2">
                            Powered by <a href="https://mobilarge.com.tr" target="_blank" rel="noopener noreferrer" className="font-semibold text-muted-foreground/60 hover:text-muted-foreground hover:underline">Mobilarge</a>
                        </p>
                    </motion.div>
                </div>
            </div>

            {/* Şirket Seçim Modalı */}
            {showCompanyModal && (
                <CompanySelectModal
                    onClose={() => setShowCompanyModal(false)}
                    onSelect={(tenantConfig) => {
                        setShowCompanyModal(false);
                        setSsoLoading(true);
                        loginWithKeycloak(tenantConfig);
                    }}
                />
            )}
        </div>
    );
};

export default LoginPage;
